import moment from 'moment'
import AuthMixin from '@/components/mixins/auth-mixin'
import EventBus from '@/components/mixins/event-bus-mixin'
import { useMixpanel } from '@base-shared/composables/use-mixpanel'
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth'

export default {
  name: 'accountInformation',
  inject: [
    '$currentUser',
    '$utils',
    '$api',
    '$apiError',
    '$signedInUserDetails'
  ],
  props: {
    userInfoProp: {
      default () { return {} }
    },
    userDetailsProp: {
      default () { return {} }
    },
    userAccountsProp: {
      default () { return [] }
    }
  },
  watch: {
    userInfoProp () {
      this.setDefaultData()
    },
    userDetailsProp () {
      this.setDefaultData()
    }
  },
  mixins: [AuthMixin],
  computed: {
    accountId () {
      return this.userInfo?._edit?.accountInformation?.groupId ?? this.$currentUser?.getUserGroupId() ?? ''
    },
    stages () {
      const stages = this.$utils.getFormattedStages() ?? []

      return Object.keys(stages).map((key) => stages[key])
    },
    selectedStage () {
      return useCognitoAuthStore().sessionStage ?? CONFIG.GLOBAL_STAGE
    },
    dataStorageRegionName () {
      if ((!this.selectedStage && !this.userInfo?.dataResidency) || !this.stages?.length) {
        return ''
      }

      // Find the stage based on userInfo dataResidency or selectedStage
      const stage = this.userInfo?.dataResidency
        ? this.stages.find((s) => s.code === this.userInfo.dataResidency)
        : this.stages.find((s) => s.stage === this.selectedStage);

      return stage?.translationKey ? this.$t(stage.translationKey) : ''
    }
  },
  data () {
    return {
      userDetails: {},
      userInfo: {},
      userAccounts: [],
      panelHeaderTitle: this.$t('label.accountInformation'),
      content: 'accountInfo',
      contentOptions: {
        accountInfo: 'accountInfo',
        planDowngraded: 'planDowngraded',
        accountDeleted: 'accountDeleted'
      },
      enableEditAccountName: false
    }
  },
  methods: {
    emitEvent (action, data) {
      if (action !== null || action.length > 0) {
        this.$emit('user-account-change', { action, payload: { data } })
      }
    },
    localizedMoment (date) {
      return moment(date).locale(this.$dataStore.getLanguage().code)
    },
    setDefaultData () {
      this.userDetails = this.$signedInUserDetails
      this.userInfo = this.filterUserInfo(this.userInfoProp)
      this.userAccounts = this.userAccountsProp
      this.enableEditAccountName = this.$currentUser.hasGroupUserAdmin() || (this.$currentUser.isStandAloneUser() || this.$currentUser.isFreeUser())
    },
    filterUserInfo (userInfoSrc = {}) {
      const userInfo = userInfoSrc

      userInfo._edit.accountInformation = {}
      userInfo._edit.accountInformation.groupId = this.$currentUser?.getUserGroupId() ?? null
      userInfo._edit.accountInformation.accountName = userInfo?.accountName ?? userInfo?.address?.companyName ?? null
      userInfo._edit.accountInformation.accountPlanName = userInfo._edit.plan.currentPlan
      userInfo._edit.accountInformation.users = `${userInfo.usage.numberOfUsers} / ${userInfo.activePlan.maxUsers}`
      userInfo._edit.accountInformation.subscription = null
      userInfo._edit.accountInformation.options = null
      userInfo._edit.accountInformation.downgradeAccountEnabled = false
      userInfo._edit.accountInformation.deleteAccountEnabled = false

      // Set storage value
      const usedStorageInGB = `${(userInfo.usage.usedStorageKB / 1000000).toFixed(1)} GB`
      const maxStorageInGB = `${Math.floor(userInfo.activePlan.maxStorageKB / 1000000)} GB`
      userInfo._edit.accountInformation.storage = `${usedStorageInGB} of ${maxStorageInGB}`

      // Set installed capacity
      userInfo._edit.accountInformation.installedOrCapacity = `${userInfo.usage.numberOfPlayers} of `
      if (userInfo.activePlan && userInfo.activePlan.maxPlayers) {
        userInfo._edit.accountInformation.installedOrCapacity += userInfo.activePlan.maxPlayers
      } else {
        userInfo._edit.accountInformation.installedOrCapacity += 0
      }

      const accountOptions = [];

      // Set Proof of play in account options if enabled
      if (userInfo.features.proofOfPlay) {
        accountOptions.push(`Proof of Play ${userInfo.features.proofOfPlay}`);
      }

      // Set Custom RSS in account options if enabled
      if (userInfo.features.custom_rss) {
        accountOptions.push('Custom RSS');
      }

      // Set account options
      if (accountOptions.length > 0) {
        userInfo._edit.accountInformation.options = accountOptions.join(', ');
      }

      // Set Plan Name
      userInfo._edit.accountInformation.accountPlanName = userInfo._edit.plan.currentPlanName

      const expiryDate = userInfo.activePlan.expiryDate
        ? this.localizedMoment(userInfo.activePlan.expiryDate).format('DD MMM YYYY')
        : this.$t('label.naText')

      switch (userInfo._edit.plan.currentPlan) {
        case 'discovery':
          userInfo._edit.accountInformation.installedOrCapacity = `${userInfo.usage.numberOfPlayers} ${this.$t('label.installedOrCapacityUnlimitedMaxValue')}`
          userInfo._edit.accountInformation.subscription = this.$t('label.freeVersionNA')
          userInfo._edit.accountInformation.deleteAccountEnabled = true
          break
        case 'premium':
          userInfo._edit.accountInformation.installedOrCapacity = `${userInfo.usage.numberOfPlayers} ${this.$t('label.player', userInfo.usage.numberOfPlayers)}`
          userInfo._edit.accountInformation.subscription = this.$t('label.subscriptionMonthlyAutoRenew')
          userInfo._edit.accountInformation.downgradeAccountEnabled = this.$currentUser.hasGroupUserAdmin()
          userInfo._edit.accountInformation.deleteAccountEnabled = this.$currentUser.hasGroupUserAdmin()
          break
        case 'enterprise':
        case 'enterprise-widgets':
        case 'enterprise-systems':
        case 'enterprise-kiosk': {
          userInfo._edit.accountInformation.subscription = expiryDate
          userInfo._edit.accountInformation.downgradeAccountEnabled = false
          userInfo._edit.accountInformation.deleteAccountEnabled = this.$currentUser.hasGroupDevelopment()
          break
        }
        case 'enterprise-demo':
          userInfo._edit.accountInformation.subscription = expiryDate
          userInfo._edit.accountInformation.downgradeAccountEnabled = false
          userInfo._edit.accountInformation.deleteAccountEnabled = true
          break
        case 'enterprise-trial':
          userInfo._edit.accountInformation.subscription = expiryDate
          userInfo._edit.accountInformation.downgradeAccountEnabled = false
          userInfo._edit.accountInformation.deleteAccountEnabled = true
          break
        default:
      }

      return userInfo
    },
    handleEditAccountNameIconClick () {
      this.emitEvent('EDIT_ACCOUNT_NAME', { userInfo: this.userInfo })
    },
    handleDowngradePlan () {
      const confirmationMessage = this.$t('label.downgradeAccount')
      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'warning',
        description: confirmationMessage,
        callbacks: {
          eventOKButtonClick: () => {
            useMixpanel().track(`Downgrade to ${this.$utils.getPlanNameFromBundledPlan(this.$utils.options.plans.DISCOVERY)}`)

            const downgradePlanRequest = () => {
              const requestData = { plan: this.$utils.options.plans.DISCOVERY }

              this.$utils.mainLoader(true)
              this.$api.put(`${CONFIG.API_BASE_URL}/group/plan`, requestData)
                .then((response) => {
                  this.getUserUserInfoFromBackend({
                    callback: () => {
                      // Show downgraded plan description content in 5 seconds
                      const self = this
                      self.content = this.contentOptions.planDowngraded

                      window.setTimeout(() => {
                        self.content = this.contentOptions.accountInfo
                      }, 5000)
                      self.$utils.mainLoader(false)
                    }
                  })
                })
                .catch((error) => {
                  this.$utils.mainLoader(false)
                  this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { downgradePlanRequest() } })
                })
            }
            downgradePlanRequest()
          }
        }
      })
    },
    getDeleteAccountDialogConfig () {
      const deleteAccountEmail = this.userDetails.email
      const accountName = this.userInfo?.accountName ?? this.userInfo?.address?.companyName ?? null
      const baseTitle = this.$t('confirm.deleteAccount')
      const deleteAccountMessage = [
        this.$t('label.deleteAccountTitleThisActionWill'),
        this.$t('label.deleteAccountImplications.deleteAccount'),
        `${this.$t('label.deleteAccountImplications.deleteUser')} ${deleteAccountEmail}`
      ]

      // Distributor account: cannot delete
      if (this.$currentUser.hasGroupDistributor()) {
        return {
          type: 'error',
          title: baseTitle,
          alert: { message: this.$t('label.deleteAccountWithDistributorRightsMessage') },
          callbacks: { eventOnClose: () => {} }
        }
      }

      // Multiple users in account: cannot delete
      if (this.userInfo.hasMultipleUsers) {
        return {
          type: 'error',
          title: baseTitle,
          alert: { message: this.$t('label.deleteAccountWithMultipleUsersMessage') },
          callbacks: { eventOnClose: () => {} }
        }
      }

      // User in multiple accounts: delete just this account
      const userInMultipleAccounts = this.userAccounts.length > 1 &&
        this.userAccounts.find((account) => account.groupId === this.userDetails.groupId)
      if (userInMultipleAccounts) {
        return {
          type: 'warning',
          title: baseTitle,
          description: accountName,
          callbacks: {
            eventOnClose: () => {},
            eventOKButtonClick: () => { this.deleteAccountRequest() }
          }
        }
      }

      // Single user, single account: delete both user and account
      return {
        type: 'warning',
        title: baseTitle,
        description: accountName,
        alert: { message: deleteAccountMessage },
        callbacks: {
          eventOnClose: () => {},
          eventOKButtonClick: () => { this.deleteAccountRequest() }
        }
      }
    },
    handleDeleteAccount () {
      const deleteAccountRequest = () => {
        this.$utils.mainLoader(true)
        this.$api.delete(`${CONFIG.API_BASE_URL}/group/me`)
          .then(response => {
            const self = this
            self.content = self.contentOptions.accountDeleted
            self.$utils.mainLoader(false)
            window.setTimeout(() => { self.signOut() }, 3000)
          })
          .catch((error) => {
            this.$utils.mainLoader(false)
            const errorMessage = error.response.data?.message ?? ''
            let dialogErrorMessage = ''

            if (errorMessage.includes('cannot delete user as you are the only user in the account')) {
              dialogErrorMessage = this.$t('label.youCannotDeleteUserAsOnlyUserInAccount')
            } else {
              dialogErrorMessage = errorMessage
            }

            this.$apiError.handleAPIRequestError(error, {
              originalRequest: () => { deleteAccountRequest() },
              showDialogNotification: true,
              dialogErrorMessage
            })
          })
      }

      // Store reference to use in config
      this.deleteAccountRequest = deleteAccountRequest

      const dialogConfig = this.getDeleteAccountDialogConfig()
      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', dialogConfig)
    },
    handleCopyAccountId () {
      if (!this.$refs.accountId && !this.$refs.accountId.value) return
      navigator.clipboard.writeText(this.$refs.accountId.value).catch(() => alert(`Cannot copy Account ID: ${this.$refs.accountId.value}`))
    },
    /**
     * Silently sets accountName from companyName when both
     * companyName and countryCode are present.
     */
    trySilentAccountNamePrefill () {
      if (
        !this.enableEditAccountName ||
        !this.userInfo || this.userInfo.accountName ||
        !this.userInfo.address?.companyName || !this.userInfo.address?.countryCode
      ) {
        return
      }

      this.$utils.mainLoader(true)
      this.$api
        .put(`${CONFIG.API_BASE_URL}/group/info`, {
          accountName: this.userInfo.address.companyName
        })
        .then(() => {
          this.userInfo.accountName = this.userInfo.address?.companyName
          console.log('trySilentAccountNamePrefill', this.userInfo)
        })
        .catch((error) => {
          console.error('Failed to set company name as account name', error);
        })
        .finally(() => {
          this.$utils.mainLoader(false)
        });
    }
  },
  mounted () {
    this.setDefaultData()
    this.trySilentAccountNamePrefill()
  }
}
